"use server";

import { getAuthHeaders, getCacheOptions } from "@/lib/data/cookies";
import { withSession } from "@/lib/data/session";
import { sdk } from "@/lib/medusa";
import { getRegion } from "@/src/lib/data/regions";
import { HttpTypes, RemoteQueryFilters } from "@medusajs/types";
import { ProductReviewsResponse } from "../types/review";
import { mapCurrencyforBackend } from "@/lib/util/utils";
import logger from "@/lib/util/logger";

type GetProductsListParams = {
  pageParam?: number;
  queryParams?: RemoteQueryFilters<'products'>;
  countryCode?: string;
  regionId?: string;
  currency_code?: string;
  province?: string;
  municipality?: string;
};

export type Product = HttpTypes.StoreProduct & {
  price: string;
  originalPrice: string;
};

type GetProductsListResponse = Promise<{
  response: { products: Product[]; count: number };
  nextPage: number | null;
  queryParams?: HttpTypes.StoreProductListParams;
}>;

export const getPopularProductsList = async ({
  pageParam = 1,
  queryParams,
  countryCode,
  regionId,
  currency_code,
  province,
  municipality,
}: GetProductsListParams): GetProductsListResponse => {
  const limit = queryParams?.limit || 12;

  // Use regionId directly if provided, otherwise get region from countryCode
  let effectiveRegionId = regionId;
  if (!effectiveRegionId && countryCode) {
    const region = await getRegion(countryCode);
    effectiveRegionId = region?.id;
  }

  if (!effectiveRegionId) {
    return {
      response: { products: [], count: 0 },
      nextPage: null,
    };
  }

  type Response = { products: []; count: number };
  return sdk.client
    .fetch<Response>(`/store/products/popular`, {
      method: "GET",
      query: {
        limit,
        offset: (pageParam - 1) * limit,
        region_id: effectiveRegionId,
        country_code: countryCode,
        ...(province && { province }),
        ...(municipality && { municipality }),
        ...(municipality && { municipality }),
        currency_code: mapCurrencyforBackend(
          currency_code?.toLowerCase() ?? "EUR"
        ),
        // order: "product_price",
        // Include variants.prices.* to get all currency prices for multi-currency support
        fields:
          "*,variants.prices.*,variants.calculated_price.*,variants.inventory_quantity,variants.*,+title,+thumbnail,+handle,images.*",
        ...queryParams,
      },
      cache: "no-store",
    })
    .then(({ products, count }) => {
      const nextPage = count > pageParam * limit ? pageParam + 1 : null;

      return {
        response: {
          products,
          count,
        },
        nextPage,
        queryParams,
      };
    });
};

export const getTagByName = async (name: string) => {
  interface Response {
    product_tags: HttpTypes.StoreProductTag[];
  }
  return sdk.client
    .fetch<Response>(`/store/product-tags`, {
      method: "GET",
      query: {
        value: name,
      },
      cache: "no-store",
    }).then(({ product_tags }) => product_tags[0]?.id)
    .catch(() => null);
};

export const getProductsList = async ({
  pageParam = 1,
  queryParams,
  countryCode,
  regionId,
  currency_code,
  province,
  municipality,
}: GetProductsListParams): GetProductsListResponse => {
  const limit = queryParams?.limit || 12;

  // Use regionId directly if provided, otherwise get region from countryCode
  let effectiveRegionId = regionId;
  if (!effectiveRegionId && countryCode) {
    const region = await getRegion(countryCode);
    effectiveRegionId = region?.id;
  }

  if (!effectiveRegionId) {
    return {
      response: { products: [], count: 0 },
      nextPage: null,
    };
  }

  type Response = { products: []; count: number };
  return sdk.client
    .fetch<Response>(`/store/products/list`, {
      method: "GET",
      query: {
        limit,
        offset: (pageParam - 1) * limit,
        country_code: countryCode,
        region_id: effectiveRegionId,
        ...(province && { province }),
        ...(municipality && { municipality }),
        currency_code: mapCurrencyforBackend(
          currency_code?.toLowerCase() ?? "EUR"
        ),
        // order: "product_price",
        // Include variants.prices.* to get all currency prices for multi-currency support
        fields:
          "*,tags.*,variants.prices.*,variants.calculated_price.*,variants.inventory_quantity,variants.*,+title,+thumbnail,+handle,images.*",
        ...queryParams,
      },
      cache: "no-store",
    })
    .then(({ products, count }) => {
      const nextPage = count > pageParam * limit ? pageParam + 1 : null;

      return {
        response: {
          products,
          count,
        },
        nextPage,
        queryParams,
      };
    });
};

export const getProductByHandle = async (
  handle: string,
  countryCode?: string,
  regionId?: string
) => {
  // Use regionId directly if provided, otherwise get region from countryCode
  let effectiveRegionId = regionId;
  if (!effectiveRegionId && countryCode) {
    const region = await getRegion(countryCode);
    effectiveRegionId = region?.id;
  }

  if (!effectiveRegionId) {
    return null;
  }

  interface Response {
    products: HttpTypes.StoreProduct[];
  }

  return sdk.client
    .fetch<Response>(`/store/products`, {
      method: "GET",
      query: {
        handle,
        region_id: effectiveRegionId,
        // Include variants.prices.* for multi-currency support
        fields:
          "*variants.calculated_price,*variants.prices.*,*variants.inventory_quantity,+title,+thumbnail,+handle,+description,+images",
      },
      cache: "no-store",
    })
    .then(({ products }) => products[0])
    .catch(() => null);
};

type GetCategoriesListParams = {
  pageParam?: number;
  queryParams?: HttpTypes.StoreProductCategoryListParams;
};

export const getCategoriesList = async ({
  pageParam = 1,
  queryParams,
}: GetCategoriesListParams) => {
  const limit = queryParams?.limit || 12;

  interface Response {
    product_categories: HttpTypes.StoreProductCategory[];
    count: number;
  }

  return sdk.client
    .fetch<Response>(`/store/product-categories`, {
      method: "GET",
      query: {
        limit,
        offset: (pageParam - 1) * limit,
        ...queryParams,
        fields: "name",
      },
      cache: "no-store",
    })
    .then(({ product_categories, count }) => {
      const nextPage = count > pageParam * limit ? pageParam + 1 : null;

      return {
        response: {
          product_categories,
          count,
        },
        nextPage,
        queryParams,
      };
    });
};

type GetCollectionsListParams = {
  pageParam?: number;
  queryParams?: HttpTypes.StoreCollectionListParams;
};

export const getCollectionsList = async ({
  pageParam = 1,
  queryParams,
}: GetCollectionsListParams) => {
  const limit = queryParams?.limit || 12;

  interface Response {
    collections: HttpTypes.StoreCollection[];
    count: number;
  }

  return sdk.client
    .fetch<Response>(`/store/collections`, {
      method: "GET",
      query: {
        limit,
        offset: (pageParam - 1) * limit,
        ...queryParams,
      },
      cache: "no-store",
    })
    .then(({ collections, count }) => {
      const nextPage = count > pageParam * limit ? pageParam + 1 : null;

      return {
        response: {
          collections,
          count,
        },
        nextPage,
        queryParams,
      };
    });
};

export const getProductsByCollectionHandle = async ({
  handle,
  pageParam = 1,
  queryParams,
  countryCode,
  regionId,
  currency_code,
  province,
  municipality,
}: {
  handle: string;
} & GetProductsListParams): GetProductsListResponse => {
  const collectionRes = await getCollectionsList({
    queryParams: { handle },
  });

  const collectionId = collectionRes.response.collections[0]?.id;

  if (!collectionId) {
    return {
      response: { products: [], count: 0 },
      nextPage: null,
    };
  }

  return getProductsList({
    pageParam,
    queryParams: { ...queryParams, collection_id: [collectionId] },
    countryCode,
    regionId,
    province,
    municipality,
    currency_code: mapCurrencyforBackend(currency_code?.toLowerCase() ?? "USD"),
  });
};

export const addProductReview = async ({
  rating,
  comment,
  product_id,
}: {
  rating: number;
  comment?: string;
  product_id: string;
}) => {
  const headers = {
    ...(await getAuthHeaders()),
  };

  return withSession(() =>
    sdk.client
      .fetch("/store/reviews", {
        method: "POST",
        body: {
          rating,
          product_id,
          comment: comment || "",
        },
        headers,
      })
      .then((res) => {
        return res;
      })
  );
};

export const getProductReviews = async ({
  productId,
  limit = 10,
  offset = 0,
}: {
  productId: string;
  limit?: number;
  offset?: number;
}) => {
  const headers = {
    ...(await getAuthHeaders()),
  };

  const next = {
    ...(await getCacheOptions(`product-reviews-${productId}`)),
  };

  return sdk.client.fetch<ProductReviewsResponse>(
    `/store/products/${productId}/reviews`,
    {
      headers,
      query: {
        limit,
        offset,
        order: "-created_at",
      },
      next,
    }
  );
};

export const getCustomerReviews = async () => {
  const headers = {
    ...(await getAuthHeaders()),
  };

  const next = {
    ...(await getCacheOptions(`customer-reviews`)),
  };

  return sdk.client.fetch<{ reviews: any[] }>(
    `/store/reviews`,
    {
      method: "GET",
      headers,
      next,
    }
  );
};

export const subscribeToProduct = async ({
  variant_id,
  email,
}: {
  variant_id: string;
  email: string;
}) => {
  const headers = {
    ...(await getAuthHeaders()),
  };

  // Was a floating promise: neither awaited nor returned, so failures surfaced
  // as unhandled rejections and the caller could never know it had failed.
  return withSession(() =>
    sdk.client.fetch(`/store/restock-subscriptions`, {
      method: "POST",
      body: {
        variant_id,
        email,
      },
      headers,
    })
  );
};

export const getProductsByTag = async ({
  tag,
  pageParam = 1,
  queryParams,
  countryCode,
  regionId,
  currency_code,
  province,
  municipality,
}: {
  tag: string;
} & GetProductsListParams): GetProductsListResponse => {
  const tagID = await getTagByName(tag);
  logger.debug("Tag ID for tag", { tag, tagID });

  if (!tag) {
    return {
      response: { products: [], count: 0 },
      nextPage: null,
    };
  }

  return getProductsList({
    pageParam,
    queryParams: { ...queryParams, tags: [tagID] },
    countryCode,
    regionId,
    province,
    municipality,
    currency_code: mapCurrencyforBackend(currency_code?.toLowerCase() ?? "USD"),
  });
};
